import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { NavBar } from '../components/NavBar';
import { getDifficultyLabel } from '../utils/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Target,
  Calendar,
  Dumbbell,
  Clock,
  ChevronRight,
  Play,
  Pause,
  CheckCircle,
  Trophy,
  Zap,
  Lock,
  DollarSign,
  Unlock,
  Crown,
  Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const LOGO_URL = '/logo.webp';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  ultimate_fitness: 'Ultimate Fitness',
  strength: 'Strength'
};

const goalColors = {
  weight_loss: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  muscle_building: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  endurance: 'bg-green-500/20 text-green-400 border-green-500/30',
  general_fitness: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  ultimate_fitness: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  strength: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const difficultyColors = {
  beginner: 'bg-green-500/20 text-green-400 border-green-500/30',
  intermediate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  advanced: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export const ProgramsPage = () => {
  const { api, user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [recommendedPrograms, setRecommendedPrograms] = useState([]);
  const [myProgram, setMyProgram] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [loading, setLoading] = useState(true);

  const topRecommended = useMemo(
    () => recommendedPrograms.filter(p => p.is_recommended).slice(0, 3),
    [recommendedPrograms],
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [programsRes, myProgramRes, purchasesRes, recommendedRes] = await Promise.all([
        api().get('/programs'),
        api().get('/programs/my/current'),
        api().get('/payments/purchases'),
        api().get('/programs/recommended')
      ]);
      setPrograms(programsRes.data);
      setMyProgram(myProgramRes.data);
      setPurchases(purchasesRes.data || []);
      setRecommendedPrograms(recommendedRes.data || []);
    } catch {
      // silent
    }
    setLoading(false);
  };

  const isPurchased = (programId) => {
    return purchases.some(p => p.program_id === programId);
  };

  const getPrice = (program) => {
    return program?.price || 49.99;
  };

  const pauseProgram = async () => {
    try {
      await api().put('/programs/my/pause');
      toast.success('Program paused');
      fetchData();
    } catch (err) {
      toast.error('Failed to pause program');
    }
  };

  const resumeProgram = async () => {
    try {
      await api().put('/programs/my/resume');
      toast.success('Program resumed!');
      fetchData();
    } catch (err) {
      toast.error('Failed to resume program');
    }
  };

  const goals = ['all', 'weight_loss', 'muscle_building', 'endurance', 'general_fitness', 'strength'];
  const durations = ['all', 8, 10, 12];

  const filteredPrograms = programs.filter(p => {
    const matchesGoal = selectedGoal === 'all' || p.goal === selectedGoal;
    const matchesDuration = selectedDuration === 'all' || p.duration_weeks === selectedDuration;
    return matchesGoal && matchesDuration;
  });

  const currentWeekProgress = myProgram?.user_program 
    ? (myProgram.user_program.current_week / myProgram.program.duration_weeks) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-black" data-testid="programs-page">
      <NavBar title="Programs" />

      <main className="container mx-auto px-6 py-8">
        {/* Current Program Banner */}
        {myProgram && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 rounded-2xl p-6 mb-10"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 bg-primary/20 flex items-center justify-center">
                  <Trophy className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <p className="text-primary text-sm uppercase tracking-wider font-heading">Current Program</p>
                  <h3 className="font-heading text-2xl text-white uppercase tracking-wider">{myProgram.program.title}</h3>
                  <p className="text-muted-foreground">Week {myProgram.user_program.current_week} of {myProgram.program.duration_weeks}</p>
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground text-sm">Progress</span>
                  <span className="text-primary font-heading">{Math.round(currentWeekProgress)}%</span>
                </div>
                <Progress value={currentWeekProgress} className="h-3" />
              </div>
              <div className="flex gap-3">
                <Link to={`/program/${myProgram.program.id}`}>
                  <Button className="bg-primary text-primary-foreground font-heading tracking-wider">
                    <Play className="mr-2 h-4 w-4" />
                    Continue
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={pauseProgram}
                >
                  <Pause className="mr-2 h-4 w-4" />
                  Pause
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Recommended Programs Section */}
        {recommendedPrograms.length > 0 && user?.fitness_goals?.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <Zap className="h-6 w-6 text-primary" />
              <h3 className="font-heading text-2xl text-white uppercase tracking-wider">
                Recommended <span className="text-primary">For You</span>
              </h3>
            </div>
            <p className="text-muted-foreground mb-6">Based on your fitness goals and experience level</p>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {topRecommended.map((program) => {
                const purchased = isPurchased(program.id);
                const price = getPrice(program);
                const recIncluded =
                  program.premium_accessible === true &&
                  (user?.is_premium || user?.is_admin) &&
                  !purchased;
                return (
                  <Link key={program.id} to={`/program/${program.id}`} className="block">
                    <div className="bg-gradient-to-r from-primary/20 to-primary/5 border-2 border-primary/50 rounded-2xl p-4 hover:border-primary transition-colors">
                      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
                        <Badge className="bg-primary text-black">Recommended</Badge>
                        {purchased && (
                          <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                            <Unlock className="h-3 w-3 mr-1" />
                            Owned
                          </Badge>
                        )}
                        {recIncluded && (
                          <Badge className="bg-primary text-black border border-primary shadow-md shadow-primary/40">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Included
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-heading text-lg text-white uppercase tracking-wider mb-1">{program.title}</h4>
                      <p className="text-muted-foreground text-sm mb-3">{program.duration_weeks} weeks · {goalLabels[program.goal]}</p>
                      {!purchased && !recIncluded && (
                        <p className="text-primary font-heading text-xl">${price}</p>
                      )}
                      {recIncluded && (
                        <p className="text-primary font-heading text-sm uppercase tracking-wider flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5" /> With Premium
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Training <span className="text-primary">Programs</span>
          </h2>
          <p className="text-muted-foreground text-lg">Choose a structured program tailored to your fitness goals</p>
        </motion.div>

        {/* Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-muted-foreground text-xs uppercase tracking-[0.2em] font-heading mb-2">
              Goal
            </label>
            <Select value={selectedGoal} onValueChange={setSelectedGoal}>
              <SelectTrigger
                data-testid="filter-goal-select"
                className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
              >
                <SelectValue placeholder="All Goals" />
              </SelectTrigger>
              <SelectContent
                className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
                position="popper"
              >
                {goals.map((goal) => (
                  <SelectItem
                    key={goal}
                    value={goal}
                    data-testid={`filter-goal-${goal}`}
                    className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                  >
                    {goal === 'all' ? 'All Goals' : goalLabels[goal]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className="block text-muted-foreground text-xs uppercase tracking-[0.2em] font-heading mb-2">
              Duration
            </label>
            <Select
              value={String(selectedDuration)}
              onValueChange={(v) => setSelectedDuration(v === 'all' ? 'all' : Number(v))}
            >
              <SelectTrigger
                data-testid="filter-duration-select"
                className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
              >
                <SelectValue placeholder="All Durations" />
              </SelectTrigger>
              <SelectContent
                className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
                position="popper"
              >
                {durations.map((duration) => (
                  <SelectItem
                    key={duration}
                    value={String(duration)}
                    data-testid={`filter-duration-${duration}`}
                    className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                  >
                    {duration === 'all' ? 'All Durations' : `${duration} Weeks`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrograms.map((program, i) => {
            const purchased = isPurchased(program.id);
            const price = getPrice(program);
            const isActive = myProgram?.program?.id === program.id;
            // Premium gate: program is paywalled AND viewer is not premium.
            // Admin always bypasses (they need to be able to verify content).
            const premiumLocked = program.premium_only && !user?.is_premium && !user?.is_admin;
            // "Included with Premium" — this program is bundled into the
            // subscription for premium members. Skip if they already bought
            // it (owned trumps included) or if they're not premium/admin.
            const premiumIncluded =
              program.premium_accessible === true &&
              (user?.is_premium || user?.is_admin) &&
              !purchased;
            
            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-card border border-border rounded-2xl overflow-hidden group card-hover"
                data-testid={`program-card-${program.id}`}
              >
                <div className="relative h-48">
                  <img 
                    src={program.image_url}
                    alt={program.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className={`${goalColors[program.goal]} border`}>
                      {goalLabels[program.goal]}
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Badge className={`${difficultyColors[program.difficulty]} border`}>
                      {getDifficultyLabel(program.difficulty)}
                    </Badge>
                    {purchased && (
                      <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                        <Unlock className="h-3 w-3 mr-1" />
                        Owned
                      </Badge>
                    )}
                    {premiumIncluded && (
                      <Badge
                        className="bg-primary text-black border border-primary shadow-md shadow-primary/40"
                        data-testid={`program-included-badge-${program.id}`}
                      >
                        <Sparkles className="h-3 w-3 mr-1" />
                        Included
                      </Badge>
                    )}
                    {program.premium_only && (
                      <Badge className="bg-primary/90 text-black border border-primary">
                        <Crown className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <div className="flex items-center gap-2 text-white">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span className="font-heading text-2xl">{program.duration_weeks}</span>
                      <span className="text-white/70">Weeks</span>
                    </div>
                  </div>
                  {/* Price Tag — hidden for owned or premium-included programs */}
                  {!purchased && !premiumIncluded && (
                    <div className="absolute bottom-4 right-4">
                      <div className="bg-primary px-3 py-1 flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-black" />
                        <span className="font-heading text-xl text-black">{price}</span>
                      </div>
                    </div>
                  )}
                  {premiumIncluded && (
                    <div className="absolute bottom-4 right-4">
                      <div className="bg-primary/95 px-3 py-1 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-black" />
                        <span className="font-heading text-xs uppercase tracking-wider text-black">
                          With Premium
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">{program.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{program.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Dumbbell className="h-4 w-4 text-primary" />
                      <span>{program.weeks?.length * 4 || 0}+ Workouts</span>
                    </div>
                  </div>

                  {premiumLocked ? (
                    <Link to="/subscription" className="block">
                      <Button
                        className="w-full bg-zinc-800 text-primary hover:bg-zinc-700 font-heading tracking-wider uppercase border border-primary/40"
                        data-testid={`program-premium-lock-${program.id}`}
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Unlock with Premium
                      </Button>
                    </Link>
                  ) : isActive ? (
                    <Button 
                      className="w-full bg-green-500 text-white font-heading tracking-wider uppercase hover:bg-green-600"
                      disabled
                      data-testid={`program-active-${program.id}`}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Currently Active
                    </Button>
                  ) : purchased ? (
                    <Link to={`/program/${program.id}`} className="block">
                      <Button 
                        className="w-full bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
                        disabled={!!myProgram}
                        data-testid={`program-start-${program.id}`}
                      >
                        <Zap className="mr-2 h-4 w-4" />
                        {myProgram ? 'Finish Current First' : 'Start Program'}
                      </Button>
                    </Link>
                  ) : premiumIncluded ? (
                    <Link to={`/program/${program.id}`} className="block">
                      <Button
                        className="w-full bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
                        disabled={!!myProgram}
                        data-testid={`program-included-start-${program.id}`}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        {myProgram ? 'Finish Current First' : 'Start · Included'}
                      </Button>
                    </Link>
                  ) : (
                    <Link to={`/program/${program.id}`} className="block">
                      <Button 
                        className="w-full bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
                        data-testid={`program-purchase-${program.id}`}
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Purchase - ${price}
                      </Button>
                    </Link>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredPrograms.length === 0 && !loading && (
          <div className="text-center py-20">
            <Target className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No programs found matching your criteria</p>
          </div>
        )}
      </main>
    </div>
  );
};
